import { useQueryParameters } from '@/hooks/useQueryParameters';
import { getCollaborationTemplateComponent, getStoreByTemplate } from '@/utils/templateUtils';
import { Transition } from '@headlessui/react';
import { RequestDto } from '@oncolens/oncolens-ui/lib/dto/event/request';
import { useHydrateStore } from '@oncolens/oncolens-ui/lib/hooks/common/useHydrateForm';
import React, { useEffect } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router';
import { getFullRequest, getRequest } from '../api/getRequest';
import { useGetTemplate } from '../api/getTemplate';
import { useWorkflow } from '../api/getWorkflow';
import NewRequestAutosave from '../components/NewRequest/NewRequestSubmission';
import SubmitRequest from '../components/NewRequest/Templates/osu/SubmitRequest';
import { FullCollaborationRequestDto } from '../dto/FullCollaborationRequest';

type NewRequestParams = {
	[x: string]: string;
	templateId: string;
};

export const NewRequest = () => {
  const params = useParams<NewRequestParams>();
  const requestId = useQueryParameters().get('requestId') || '';
  const navigate = useNavigate();
  const { isLoading: isTemplateListLoading, data: template } = useGetTemplate(
    params.templateId || ''
  );

  const { isLoading: isWorkflowLoading, data: workflow } = useWorkflow({
    id: template?.workflowId || '',
    options: {
      enabled: !!template?.workflowId
    }
  });

  const [storeByTemplate, hydrateStore] = getStoreByTemplate(template);

  const { updateStore, submissionId, reset } = storeByTemplate();
  // Custom updateStore implementation since the data we're getting is actually split up across a few different properties
  const { isLoading: isHydrating } = useHydrateStore<FullCollaborationRequestDto | RequestDto>({
    apiCall: () =>
      template?.component === 'OsuFormTemplate' ? getFullRequest(requestId) : getRequest(requestId),
    keys: {
      cacheKey: ['request', requestId || ''],
      formKey: ''
    },
    updateStore: (path, value) => {
      updateStore((state) => {
        hydrateStore(state, value);
      });
    },
    shouldRun: Boolean(requestId) && Boolean(template?.component)
  });

  const TemplateComponent = getCollaborationTemplateComponent(template);

  // Hydrate the form with the template and organization ID so we can use it later for saving
  useEffect(() => {
    if (!isTemplateListLoading && template && !isWorkflowLoading && workflow) {
      updateStore((store: any) => {
        store.templateId = template?.id;
        store.workflowId = workflow?.id;
        store.organizationId = workflow?.organizationId;
      });
    }
  }, [
    template,
    isTemplateListLoading,
    workflow,
    isWorkflowLoading,
    params.templateId,
    updateStore
  ]);

  useEffect(() => {
    return () => reset();
  }, [reset]);

  if (!isTemplateListLoading && !TemplateComponent && !requestId) {
    navigate('/requests');
  }

  return (
    <section className="relative w-full">
      <Transition
        show={isTemplateListLoading || isHydrating}
        className="absolute top-0 left-0 flex items-center justify-center w-full h-96"
        enter="transition-opacity duration-200"
        enterFrom="opacity-100"
        enterTo="opacity-100"
        leave="transition-opacity duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <FaSpinner className="w-6 h-6 duration-300 animate-spin" />
      </Transition>
      <Transition
        appear={true}
        show={!isTemplateListLoading && !isHydrating}
        enter="transition-opacity duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="mb-4 sm:flex">
          <div className="">
            <h1 className="text-3xl font-bold leading-8 tracking-tight text-gray-900">
              {template?.name}
            </h1>
            {submissionId && (
              <p className="mt-3 mb-3 text-lg text-gray-800 sm:mb-0">
								Please record the following submission ID:{' '}
                <strong data-cy="request-information-internalid">{submissionId}</strong>
              </p>
            )}
          </div>
          <div className="flex items-center ml-auto">
            { 
              template?.component === 'OsuFormTemplate'
              ? <SubmitRequest template={template} testSelectorPrefix="request-header" />
              : <NewRequestAutosave template={template} testSelectorPrefix="request-header" />
            }
          </div>
        </div>
        {TemplateComponent && <TemplateComponent template={template} />}
      </Transition>
    </section>
  );
};
